/**
 * Buyer exchange browse - the marketplace. SIL-1347, spec section 4.1 of
 * docs/superpowers/specs/2026-09-28-exchange-buyer-ui-design.md; visual contract
 * in docs/exchange/design-handoff/README.md section 1.
 *
 * Read-only browse of ACTIVE cross-org listings, open to any client persona
 * (buyers browse to buy; sellers may look at the market). Each card links to
 * /listings/:id for the detail and the purchase flow.
 *
 * What this screen is, and is not:
 *
 *  - **The URL is the filter state.** `validateSearch` parses `q`, `model[]`,
 *    `type[]`, `condition[]`, `region[]`, `price_min` and `price_max`, and every
 *    one of them is forwarded to `GET /api/client/marketplace`. There is NO
 *    client-side filtering, sorting or searching left on this page: the previous
 *    version narrowed the fetched rows in the browser, which silently disagreed
 *    with the server's own filters and made a shareable URL impossible.
 *  - **The tier is the server's decision.** Every price and evidence field is
 *    rendered from the tier-tagged shape the API sent (`pricing.tier`,
 *    `evidence.tier`). The page does not consult the buyer's verification status
 *    to decide what to show; it only uses it for the banner. In particular the
 *    price filter is not special-cased for an unverified caller - the server
 *    binds it to the bucketed range bounds (spec section 5.3).
 *  - **The banner is the only call to action.** Unverified cards have a single
 *    full-width "View details" and no primary button, by design.
 *
 * SIL-1169's three prototype browse variants are GONE, not flag-gated: this
 * screen is the handoff design, and a second browse implementation reachable by
 * `?variant=` is a second thing to keep in step with the server's projection.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { QueryBoundary } from '@siliconx/shared/ui/QueryBoundary';
import { EmptyState } from '@siliconx/shared/ui/state';
import { useQuery } from '@tanstack/react-query';
import type { MarketplaceListResponse } from '@siliconx/shared/dto/marketplace';
import { api, throwIfNotOk } from '../lib/api';
import { qk } from '../lib/queryKeys';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useMarginIntel } from '../lib/useMarginIntel';
import { ListingCard } from '../components/exchange/ListingCard';
import { MarketplaceFilters } from '../components/exchange/MarketplaceFilters';
import { MarketplaceResultsBoundary } from '../components/exchange/MarketplaceResultsBoundary';
import { VerifyBanner } from '../components/exchange/VerifyBanner';
import {
  EMPTY_MARKETPLACE_SEARCH,
  marketplaceApiQuery,
  marketplaceSearchSchema,
  type MarketplaceSearch,
} from '../components/exchange/marketplaceSearch';
import {
  MARKETPLACE_EMPTY_BODY,
  MARKETPLACE_EMPTY_TITLE,
  MARKETPLACE_ERROR_TITLE,
  MARKETPLACE_SEARCH_LABEL,
  MARKETPLACE_SEARCH_PLACEHOLDER,
  MARKETPLACE_SUBTITLE,
} from '@siliconx/shared/ui/buyer-listing/copy/marketplace';

/**
 * Spec section 4.1: "below about 760px the filter card moves above the results".
 * Evaluated through `matchMedia` rather than a Tailwind responsive class so the
 * reflow is a value the component branches on - see `useMediaQuery`'s note.
 */
export const MARKETPLACE_NARROW_QUERY = '(max-width: 759px)';

export const Route = createFileRoute('/marketplace')({
  component: MarketplacePage,
  validateSearch: marketplaceSearchSchema,
});

function MarketplacePage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const narrow = useMediaQuery(MARKETPLACE_NARROW_QUERY);

  // The committed `q` lives in the URL; this is the in-flight text so typing
  // does not push a history entry per keystroke. Submit (Enter or blur) commits.
  const [draftQuery, setDraftQuery] = useState(search.q ?? '');

  const apply = useCallback(
    (next: MarketplaceSearch) => {
      void navigate({ search: () => next, replace: true });
    },
    [navigate],
  );

  const commitQuery = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      apply({ ...search, q: trimmed.length > 0 ? trimmed : undefined });
    },
    [apply, search],
  );

  const clearAll = useCallback(() => {
    setDraftQuery('');
    apply(EMPTY_MARKETPLACE_SEARCH);
  }, [apply]);

  const query = useMemo(() => marketplaceApiQuery(search), [search]);

  const listingsQuery = useQuery<MarketplaceListResponse>({
    queryKey: qk.marketplace(query),
    queryFn: async () => {
      const res = await api.api.client.marketplace.$get({
        // Every parsed search param, verbatim. The route's Zod schema is the
        // only validation; nothing is dropped or rewritten on the way out.
        query,
      });
      await throwIfNotOk(res);
      return (await res.json()) as unknown as MarketplaceListResponse;
    },
  });

  const listings = listingsQuery.data?.data ?? [];

  // SIL-2543: inline margin figures for the listings on screen. The server
  // gates the listing half on buyer verification; unavailable renders nothing.
  const marginListingIds = useMemo(() => listings.map((l) => l.id), [listings]);
  const marginQuery = useMarginIntel([], marginListingIds);

  const filterCard = (
    <MarketplaceFilters
      search={search}
      listings={listings}
      onChange={apply}
      onClear={clearAll}
    />
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {/* The title is a LITERAL, and it is the CUSTOMER_NAV label for
            /marketplace verbatim - `structural-checks.test.ts` (SIL-540)
            requires both. The handoff's H1 reads "Marketplace"; the nav has
            said "Browse Listings" since the dual-profile rename, and the two
            reading the same word for the same page wins over matching the
            mock. Change them together or not at all. */}
        <PageHeader title="Browse Listings" subtitle={MARKETPLACE_SUBTITLE} pinTo="/marketplace" />
        <div className="relative w-full sm:w-[300px]">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="search"
            aria-label={MARKETPLACE_SEARCH_LABEL}
            placeholder={MARKETPLACE_SEARCH_PLACEHOLDER}
            value={draftQuery}
            onChange={(e) => setDraftQuery(e.target.value)}
            onBlur={(e) => commitQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitQuery(draftQuery);
            }}
            className="w-full rounded-md border border-border bg-card py-2 pl-8 pr-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      <VerifyBanner />

      <div
        data-testid="marketplace-layout"
        data-layout={narrow ? 'stacked' : 'sidebar'}
        className={narrow ? 'flex flex-col gap-4' : 'flex flex-row items-start gap-4'}
      >
        <div className={narrow ? 'w-full' : 'w-[220px] shrink-0'}>{filterCard}</div>

        {/* SIL-1377 - the results region, and ONLY the results region, sits
            inside a render-error boundary. The header, the search input, the
            verify banner and the filter card are all deliberately outside it:
            React unwinds to the nearest boundary, so a throw from one bad
            `ListingCard` replaces the grid with a placard and leaves the rest of
            the page - and the nav - standing. Before this, the root boundary in
            `main.tsx` caught it and blanked the whole viewport.

            It wraps `QueryBoundary` rather than sitting inside it so that a throw
            from the render function itself, not just from a card, is also caught.
            The two are not redundant: `QueryBoundary` owns the FETCH's states,
            this owns render errors on a fetch that succeeded. */}
        <div className="min-w-0 flex-1">
          <MarketplaceResultsBoundary>
            <QueryBoundary
              query={listingsQuery}
              errorTitle={MARKETPLACE_ERROR_TITLE}
              empty={(data) => data.data.length === 0}
              emptyState={
                <EmptyState title={MARKETPLACE_EMPTY_TITLE} description={MARKETPLACE_EMPTY_BODY} />
              }
            >
              {(data) => (
                <div
                  data-testid="marketplace-results"
                  className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(300px,1fr))]"
                >
                  {data.data.map((listing) => (
                    <ListingCard
                      key={listing.id}
                      listing={listing}
                      marginIntel={marginQuery.data?.listings[listing.id]}
                    />
                  ))}
                </div>
              )}
            </QueryBoundary>
          </MarketplaceResultsBoundary>
        </div>
      </div>
    </div>
  );
}
